import { hasAnyPermission, useAuthorization } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { DefaultPrimaryButton, DefaultSecondaryButton, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConstraintExtendedClient } from '@frontend/constraint/api';
import { ConstraintExtended } from '@frontend/constraint/types';
import { IdentificationClient } from '@frontend/identification/api';
import { CreateIdentification, Identification, IdentificationType } from '@frontend/identification/types';
import { CommonMessage } from '@frontend/lang';
import { SpotExtended } from '@frontend/spot/types';
import { RequirementExtendedClient, StockExtendedClient } from '@frontend/stock/api';
import { RequirementExtended, StockExtended } from '@frontend/stock/types';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedPudoTransaction } from '@frontend/transaction/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { formatConstraintData } from '../../constraint/modal/constraint-detail-sections';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import UpdateModal from '../../modal/update-modal/update-modal.component';
import { ModulesGrid } from './modules-grid/modules-grid.component';
import useSpotDetailModal from './spot-detail-modal.controller';

export const SpotGeneralSection = ({ spot }: { spot: SpotExtended }) => <ModulesGrid spotId={spot.id} />;

export const SpotActivitySection = ({ spot }: { spot: SpotExtended }) => {
    const { dispatch } = useSpotDetailModal();
    return (
        <ReactiveObjectList<ExtendedPudoTransaction>
            key='spot-transaction-list'
            fetch={(arg) =>
                TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                    account_id: spot.account_id,
                    spot_id: spot.id,
                    ...arg,
                    without_collis: 'true',
                    without_packages: 'true',
                    without_pin_codes: 'true'
                })
            }
            label={
                <FormattedMessage
                    id='pudo.active-transactions'
                    description='Label for lists of active PuDo transactions shown in activity sections'
                    defaultMessage='Active transactions'
                />
            }
            columns={{
                columns: ['senders', 'receivers', 'state'],
                headerOverwrite: new Map([
                    ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                    ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: new Map([
                    [
                        'senders',
                        (cell) => {
                            const senders = cell.row.original.senders;
                            if (!senders?.length) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={senders[0].entity_id}
                                    className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={senders[0].entity_id}
                                            entityType={EntityType.USER}
                                            display={senders[0].name}
                                            foreign={senders[0].foreign}
                                            accountId={accountIdFromResolvedEntity(senders[0])}
                                        />
                                        {senders.length > 1 && (
                                            <MultiEntityButton
                                                entities={senders.slice(1)}
                                                render={(entity) => (
                                                    <PlatformObjectBadge
                                                        id={entity.entity_id}
                                                        entityType={EntityType.USER}
                                                        display={entity.name}
                                                        foreign={entity.foreign}
                                                        accountId={accountIdFromResolvedEntity(entity)}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'receivers',
                        (cell) => {
                            const receivers = cell.row.original.receivers;
                            if (!receivers?.length) {
                                return (
                                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td
                                    key={receivers[0].entity_id}
                                    className='text-brand-primary align-center truncate p-2 text-xs'>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={receivers[0].entity_id}
                                            entityType={EntityType.USER}
                                            display={receivers[0].name}
                                            foreign={receivers[0].foreign}
                                            accountId={accountIdFromResolvedEntity(receivers[0])}
                                        />
                                        {receivers.length > 1 && (
                                            <MultiEntityButton
                                                entities={receivers.slice(1)}
                                                render={(entity) => (
                                                    <PlatformObjectBadge
                                                        id={entity.entity_id}
                                                        entityType={EntityType.USER}
                                                        display={entity.name}
                                                        foreign={entity.foreign}
                                                        accountId={accountIdFromResolvedEntity(entity)}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'state',
                        (cell) => (
                            <td className='text-foreground-default truncate p-2 text-sm font-medium'>
                                <TransactionStateBadge state={cell.row.original.state} />
                            </td>
                        )
                    ]
                ])
            }}
            dispatch={dispatch}
            type={EntityType.TRANSACTION}
            tableProps={{ hideFooter: true, maxHeight: true }}
        />
    );
};

export const SpotIdentificationSection = ({ spot }: { spot: SpotExtended }) => {
    const { dispatch, currentAccount, openModal } = useSpotDetailModal();
    const { permissions } = useAuthorization();
    const canCreate =
        permissions != null &&
        currentAccount != null &&
        hasAnyPermission(permissions, [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: currentAccount }]);

    return (
        <ReactiveObjectList<Identification>
            key='spot-identification-list'
            fetch={(arg) => IdentificationClient.fetchIdentifications({ entity_id: spot.id, entity_type: EntityType.SPOT, ...arg })}
            label={CommonMessage.OBJECTS.IDENTIFICATION.PLURAL}
            type={EntityType.IDENTIFICATION}
            eventListener={EntityType.IDENTIFICATION}
            dispatch={dispatch}
            customHeaderComponent={
                canCreate ? (
                    <div className='m-2'>
                        <DefaultPrimaryButton
                            text={
                                <FormattedMessage
                                    id='spot-detail-modal.add-identification'
                                    defaultMessage='Add identification'
                                />
                            }
                            onClick={() =>
                                openModal('spot-identification-create', () => (
                                    <UpdateModal<Identification, CreateIdentification>
                                        order={['key', 'valid_timestamp']}
                                        required={['key']}
                                        values={{
                                            key: undefined,
                                            type: IdentificationType.ALIAS,
                                            entity_id: spot.id,
                                            entity_type: EntityType.SPOT
                                        }}
                                        fieldProps={() => new Map<keyof CreateIdentification, unknown>([['valid_timestamp', { type: 'datetime' }]])}
                                        onSubmit={(v) => IdentificationClient.postIdentification(spot.account_id, v)}
                                        type={EntityType.IDENTIFICATION}
                                        label={
                                            <FormattedMessage
                                                id='spot-detail-modal.add-identification'
                                                defaultMessage='Add identification'
                                            />
                                        }
                                    />
                                ))
                            }
                        />
                    </div>
                ) : null
            }
            columns={{
                columns: ['key', 'type', 'valid_timestamp'],
                delete: {
                    deleteAction: (accountId, id) => IdentificationClient.deleteIdentification(accountId, id),
                    deleteKeys: ['account_id', 'id'],
                    requiredPermissions: currentAccount
                        ? [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.DELETE, uid: currentAccount }]
                        : []
                }
            }}
            tableProps={{ defaultHeight: true }}
        />
    );
};

export const SpotStockSection = ({ spot }: { spot: SpotExtended }) => {
    const { dispatch } = useSpotDetailModal();
    return (
        <ReactiveObjectList<StockExtended>
            key='spot-stock-list'
            fetch={(arg) => StockExtendedClient.fetchStocksExtended({ ...arg, account_id: spot.account_id, spot_id: spot.id, size: '10' })}
            type={EntityType.STOCK}
            dispatch={dispatch}
            columns={{
                columns: ['product', 'ean', 'sku', 'slot'],
                customCells: new Map([
                    [
                        'product',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.product ? (
                                    <PlatformObjectBadge
                                        entityType={EntityType.PRODUCT}
                                        id={cell.row.original.product.entity_id}
                                        display={cell.row.original.product.name}
                                        foreign={cell.row.original.product.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.product)}
                                    />
                                ) : (
                                    <span className='text-foreground-default justify-start text-sm font-medium'>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'slot',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.slot ? (
                                    <PlatformObjectBadge
                                        entityType={EntityType.SLOT}
                                        id={cell.row.original.slot.entity_id}
                                        display={cell.row.original.slot.name}
                                        parentIds={{ spot_id: spot.id }}
                                        foreign={cell.row.original.slot.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.slot)}
                                    />
                                ) : (
                                    <span className='text-foreground-default justify-start text-sm font-medium'>/</span>
                                )}
                            </td>
                        )
                    ]
                ])
            }}
            label={CommonMessage.OBJECTS.STOCK.DEFAULT}
            tableProps={{
                defaultHeight: true,
                customFooter: (table) => (
                    <div className='flex w-full items-center justify-between'>
                        <span className='text-foreground-default text-lg font-semibold'>{table.getRowModel().rows.length} results</span>
                    </div>
                )
            }}
        />
    );
};

export const SpotRequirementsSection = ({ spot }: { spot: SpotExtended }) => {
    const { dispatch } = useSpotDetailModal();
    return (
        <ReactiveObjectList<RequirementExtended>
            key='spot-requirement-list'
            fetch={(arg) => RequirementExtendedClient.fetchExtendedRequirements({ ...arg, account_id: spot.account_id, spot_id: spot.id })}
            type={EntityType.REQUIREMENT}
            dispatch={dispatch}
            columns={{
                columns: ['product', 'current', 'min', 'max'],
                headerOverwrite: new Map([
                    ['current', CommonMessage.OBJECTS.STOCK.DEFAULT],
                    ['min', CommonMessage.OBJECTS.STOCK.MIN_STOCK],
                    ['max', CommonMessage.OBJECTS.STOCK.MAX_STOCK]
                ]),
                customCells: new Map([
                    [
                        'product',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.product ? (
                                    <PlatformObjectBadge
                                        entityType={EntityType.PRODUCT}
                                        id={cell.row.original.product.entity_id}
                                        display={cell.row.original.product.name}
                                        foreign={cell.row.original.product.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.product)}
                                    />
                                ) : (
                                    <span className='text-foreground-default justify-start text-sm font-medium'>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'current',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                <DefaultBadge
                                    styles={{
                                        container: {
                                            color: `${cell.row.original.current && cell.row.original.current > cell.row.original.min ? 'bg-brand-muted' : 'bg-status-danger'}`
                                        }
                                    }}
                                    label={cell.row.original.current ? cell.row.original.current : 0}
                                />
                            </td>
                        )
                    ]
                ])
            }}
            label={
                <FormattedMessage
                    id='requirements.stock-requirement'
                    defaultMessage='Stock requirement'
                />
            }
            tableProps={{ defaultHeight: true, hideFooter: true }}
        />
    );
};

export const SpotConstraintsSection = ({ spot }: { spot: SpotExtended }) => {
    const { dispatch, viewDetailedConstraintsList } = useSpotDetailModal();
    return (
        <ReactiveObjectList<ConstraintExtended>
            key='spot-constraints'
            label={CommonMessage.OBJECTS.CONSTRAINT.PLURAL}
            fetch={(arg) => ConstraintExtendedClient.fetchExtendedConstraints({ ...arg, spot_id: spot.id, account_id: spot.account_id })}
            type={EntityType.CONSTRAINT}
            columns={{
                columns: ['module', 'slot', 'data'],
                headerOverwrite: new Map([['data', CommonMessage.OBJECTS.CONSTRAINT.DEFAULT]]),
                customCells: new Map([
                    [
                        'module',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.module ? (
                                    <PlatformObjectBadge
                                        id={cell.row.original.module.entity_id}
                                        display={cell.row.original.module.name}
                                        entityType={EntityType.MODULE}
                                        parentIds={{ spot_id: spot.id }}
                                        foreign={cell.row.original.module.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.module)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'slot',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.slot ? (
                                    <PlatformObjectBadge
                                        id={cell.row.original.slot.entity_id}
                                        display={cell.row.original.slot.name}
                                        entityType={EntityType.SLOT}
                                        parentIds={{
                                            spot_id: spot.id,
                                            ...(cell.row.original.module?.entity_id && { module_id: cell.row.original.module.entity_id })
                                        }}
                                        foreign={cell.row.original.slot.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.slot)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'data',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                <div className='flex gap-1'>
                                    <span>{(cell.row.original.type.charAt(0).toUpperCase() + cell.row.original.type.slice(1)).replace(/_/g, ' ')}:</span>
                                    <span>{formatConstraintData(cell.row.original.data, cell.row.original.type)}</span>
                                </div>
                            </td>
                        )
                    ]
                ])
            }}
            dispatch={dispatch}
            tableProps={{
                defaultHeight: true,
                customFooter: (
                    <div className='flex gap-3.5'>
                        <DefaultSecondaryButton
                            text={CommonMessage.BUTTONS.VIEW_DETAILED_LIST}
                            onClick={() => viewDetailedConstraintsList(spot.id)}
                        />
                    </div>
                )
            }}
        />
    );
};
